/**
 * THE FOUR BOOKS' COLUMNS.
 *
 * Ported from `modifyAllFilteredDataForExcel`, which was the only place these
 * four shapes were written down — and they were written there for the EXPORT
 * only, so the screen and the spreadsheet disagreed about what a row is. Here
 * they are one definition that the grid, the card view and every export read.
 *
 * ── WHY A DECLARED LIST AND NOT `Object.keys(row[0])` ──
 *
 * The logs table derived its columns from the first row's keys, so an optional
 * field missing from row 1 was hidden for every row, and a column added by a
 * migration appeared on the screen, in the XLS and in the emailed PDF without
 * anybody deciding it should. It also needed a guard against object-valued
 * columns, because a non-array object rendered as a React child throws and — with
 * no error boundary anywhere in this app — unmounts the whole root.
 *
 * A declared list closes both: a new column is a product decision, and nothing
 * that is not a string reaches a cell.
 */
import { Book, DashboardFilter } from '../state/filter-model';

export type CellKind =
  | 'text'
  | 'number'
  | 'date'
  | 'time'
  | 'pill'
  | 'names'
  | 'note';

export interface RecordColumn {
  /** The key on the row the server sends. */
  key: string;
  label: string;
  kind: CellKind;
  /** Sortable columns only. Free text on an encrypted column is not one. */
  sortable?: boolean;
  numeric?: boolean;
  /** Hidden by default; reachable from the column menu. */
  optional?: boolean;
}

const CREATED: RecordColumn[] = [
  { key: 'createdAt', label: 'Date', kind: 'date', sortable: true },
  { key: 'creationHour', label: 'Time', kind: 'time' },
];

export const RECORD_COLUMNS: Record<Book, RecordColumn[]> = {
  'Visitor Log': [
    { key: 'visitorType', label: 'Visitor type', kind: 'pill', sortable: true },
    { key: 'visitorDetails', label: 'Details', kind: 'text', sortable: true },
    { key: 'names', label: 'Name', kind: 'names' },
    { key: 'lastNames', label: 'Last name', kind: 'names' },
    { key: 'preAuthorized', label: 'Pre-authorised', kind: 'pill' },
    // Resident-portal Plan 5 (RP-5.5): derived 'Yes' / 'No' from the server
    // (log-serializer.js), never the pass id. OPTIONAL, because a twelfth
    // default column squeezed the Date cell of this table to 51px at 1280 (the
    // persona journey's screen judge, J-0.4). It is shown — on screen and in
    // every export — whenever the reader picks it from the column menu OR
    // narrows by it (columnsFor below): a filter the table did not show would
    // hide the very answer it asked for.
    { key: 'onPass', label: 'On pass', kind: 'pill', optional: true },
    { key: 'apartmentLocation', label: 'Unit', kind: 'text', sortable: true },
    { key: 'numberOfGuests', label: 'Guests', kind: 'number', numeric: true, sortable: true },
    { key: 'approval', label: 'Approval', kind: 'pill', sortable: true },
    { key: 'parkingValidation', label: 'Parking', kind: 'pill', optional: true },
    ...CREATED,
    { key: 'notes', label: 'Notes', kind: 'note', optional: true },
  ],
  'Resident Log': [
    { key: 'names', label: 'Name', kind: 'names' },
    { key: 'lastNames', label: 'Last name', kind: 'names' },
    { key: 'apartmentLocation', label: 'Unit', kind: 'text', sortable: true },
    { key: 'numberOfGuests', label: 'People', kind: 'number', numeric: true, sortable: true },
    ...CREATED,
    { key: 'departed_at', label: 'Departed', kind: 'date', optional: true },
  ],
  'Parcel Log': [
    { key: 'name', label: 'Recipient', kind: 'text', sortable: true },
    /* NOT OPTIONAL, and relabelled to the question it answers. Both of these
       were hidden by default, so the Parcel Log listed who a parcel was FOR and
       never who TOOK it — the same defect as the record detail, one surface up.
       Owner instruction, 2026-08-21: "picked up by has to be always visible
       everywhere so we know which user or non user picked up the parcel for a
       specific user." A reader who has saved a view or carries columns in the
       URL keeps their own choice; this changes what a fresh reader sees. */
    { key: 'releasedTo', label: 'Picked up by', kind: 'text' },
    { key: 'carrier', label: 'Carrier', kind: 'pill', sortable: true },
    { key: 'status', label: 'Status', kind: 'pill', sortable: true },
    { key: 'parcelCount', label: 'Parcels', kind: 'number', numeric: true, sortable: true },
    { key: 'apartmentLocation', label: 'Unit', kind: 'text', sortable: true },
    ...CREATED,
    { key: 'pickedUpAt', label: 'Collected', kind: 'date' },
    { key: 'notes', label: 'Notes', kind: 'note', optional: true },
  ],
  'Daily Log': [
    { key: 'buildingActivity', label: 'Activity', kind: 'pill', sortable: true },
    { key: 'name', label: 'Name', kind: 'text' },
    { key: 'lastName', label: 'Last name', kind: 'text' },
    { key: 'apartmentLocation', label: 'Unit', kind: 'text', sortable: true },
    { key: 'numberOfGuests', label: 'People', kind: 'number', numeric: true, sortable: true },
    { key: 'priority', label: 'Priority', kind: 'pill', sortable: true },
    ...CREATED,
    { key: 'details', label: 'Details', kind: 'note', optional: true },
    { key: 'additionalNotes', label: 'Notes', kind: 'note', optional: true },
  ],
};

/** The columns a book shows unless the reader has chosen otherwise. */
export const defaultColumns = (book: Book): string[] =>
  RECORD_COLUMNS[book].filter(c => !c.optional).map(c => c.key);

/** Every column key a book HAS. The reducer prunes a chosen set against this,
 *  so a set carried over from another book cannot survive into the URL. */
export const columnKeysFor = (book: Book): string[] =>
  RECORD_COLUMNS[book].map(c => c.key);

/**
 * Columns a FILTER brings with it. Narrowing Records by "On pass" shows the On
 * pass column (after Pre-authorised, in the book's own order) even when the
 * reader never picked it: a narrowing the table did not show would hide the
 * very answer it asked for. The export reads the same list, so the file says it
 * too.
 */
const FILTER_COLUMNS: { field: 'onPass'; column: string }[] = [{ field: 'onPass', column: 'onPass' }];

export const columnsFor = (
  book: Book,
  chosen: string[] | null,
  filter?: Partial<Pick<DashboardFilter, 'onPass'>> | null
): RecordColumn[] => {
  const base = chosen && chosen.length ? chosen : defaultColumns(book);
  const picked = RECORD_COLUMNS[book].filter(column => base.includes(column.key));
  const floor = picked.length ? picked.map(c => c.key) : defaultColumns(book);
  const wanted = [
    ...floor,
    ...FILTER_COLUMNS.filter(f => filter && filter[f.field]).map(f => f.column),
  ];
  const resolved = RECORD_COLUMNS[book].filter(column => wanted.includes(column.key));

  /* A TABLE WITH NO COLUMNS IS NOT A TABLE.
     `chosen` comes from the URL and from a saved view, and neither is guaranteed
     to name columns THIS book has: pick `visitorType` and `approval` on the
     Visitor Log, switch to the Parcel Log, and the intersection is empty. The
     reducer prunes the set for the canonical case; this is the floor underneath
     it, because a saved view or a hand-edited link reaches this function before
     anything else can. The floor is taken BEFORE a filter's own column joins,
     so a filter can never be the only column left. */
  return resolved;
};

/**
 * A CELL'S STATUS TONE.
 *
 * Ported verbatim from the logs table's `resolvePill`, including the one that is
 * a safety signal rather than a category: an emergency visitor type is coral AND
 * glows, because the reader scanning a hundred rows has to find it without
 * reading them.
 */
export type PillTone = 'brand' | 'neutral' | 'success' | 'warning' | 'alert';

export const pillFor = (
  key: string,
  raw: unknown
): { tone: PillTone; glow?: boolean } | null => {
  const value = (raw ?? '').toString().trim();
  if (!value) return null;
  const low = value.toLowerCase();

  switch (key) {
    case 'priority':
      if (low === 'high') return { tone: 'alert' };
      if (low === 'medium') return { tone: 'warning' };

      return { tone: 'neutral' };
    case 'approval':
      if (['yes', 'approved', 'approve'].includes(low)) return { tone: 'success' };
      if (['no', 'denied', 'deny', 'declined'].includes(low)) return { tone: 'alert' };
      if (low === 'pending') return { tone: 'warning' };

      return { tone: 'neutral' };
    case 'preAuthorized':
    case 'onPass':
    case 'parkingValidation':
      if (low === 'yes') return { tone: 'success' };

      return { tone: 'neutral' };
    case 'visitorType':
      if (low.includes('emergency') || low.includes('911')) {
        return { tone: 'alert', glow: true };
      }

      return { tone: 'brand' };
    case 'buildingActivity':
      return { tone: 'brand' };
    case 'status':
      if (low === 'pending') return { tone: 'warning' };
      if (low === 'picked_up') return { tone: 'success' };
      if (low === 'returned') return { tone: 'neutral' };

      return { tone: 'neutral' };
    case 'carrier':
      return { tone: 'neutral' };
    default:
      return null;
  }
};

/** Parcel statuses are stored as slugs and must never be printed as slugs — a
 *  database value on a screen is the same class of leak as a raw id. */
const STATUS_WORDS: Record<string, string> = {
  pending: 'On the shelf',
  picked_up: 'Collected',
  returned: 'Returned to sender',
};

/**
 * A cell's display text.
 *
 * BELT-AND-BRACES against a non-string value. A non-null, non-array object
 * rendered as a React child throws, and with no error boundary anywhere in this
 * app the throw unmounts the whole root — the blank "Read the log" screen. The
 * server's allow-list in `serializeLog` is the real fix; this is the belt.
 */
export const cellText = (column: RecordColumn, raw: unknown): string => {
  if (raw == null) return '';
  if (Array.isArray(raw)) return raw.filter(Boolean).join(', ');
  if (typeof raw === 'object') return '';
  const value = String(raw);
  if (column.key === 'status') return STATUS_WORDS[value] || value;

  return value;
};

/** The row's stable identity, for React keys and for the record modal. */
export const recordRef = (
  book: Book,
  /* eslint-disable @typescript-eslint/no-explicit-any */
  row: Record<string, any>
  /* eslint-enable @typescript-eslint/no-explicit-any */
): { source: string; id: string } | null => {
  if (row?.id == null) return null;
  const source =
    book === 'Parcel Log'
      ? 'package'
      : book === 'Daily Log'
      ? 'daily_log'
      : 'visitor_log';

  return { source, id: String(row.id) };
};
